import React, { useEffect, useState } from "react";
import "./topBands.css";
import dashboardService from "services/dashboard.service";

interface MetalData {
  metal: string;
  inStock: boolean;
  digiMsg: string | null;
  deliMsg: string | null;
}

const AlertIcon: React.FC = () => (
  <svg
    className="alert-icon"
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const TopBands: React.FC = () => {
  const [currentMessage, setCurrentMessage] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(false);

  /** Fetch stock status */
  const fetchStockStatus = async () => {
    try {
      const response = await dashboardService.isStockAvailable();
      const data: MetalData[] = response?.data?.data || [];

      // Group metals that share the same message, so the message is shown once
      // with all metal names comma separated instead of repeating it per metal.
      const groups = new Map<string, string[]>();
      data
        .filter((item) => item.inStock === false && item.metal)
        .forEach((item) => {
          const raw = item.digiMsg || item.deliMsg;
          if (!raw) return;
          const escaped = item.metal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          const template = new RegExp(escaped, "i").test(raw)
            ? raw.replace(new RegExp(escaped, "ig"), "{metal}")
            : `{metal}: ${raw}`;
          groups.set(template, [...(groups.get(template) || []), item.metal]);
        });

      const messages = Array.from(groups.entries()).map(([template, metals]) =>
        template.replace(/\{metal\}/g, metals.join(", "))
      );

      if (messages.length > 0) {
        setCurrentMessage(messages.join("  |  "));
        setIsActive(true);
      } else {
        setCurrentMessage(null);
        setIsActive(false);
      }
    } catch (error) {
      console.error("Error fetching stock status:", error);
      setCurrentMessage(null);
      setIsActive(false);
    }
  };

  /** Initial fetch + polling */
  useEffect(() => {
    fetchStockStatus();
    const interval = setInterval(fetchStockStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={`top-bands ${isActive ? "alert-active" : "alert-inactive"}`}>
      {currentMessage && (
        <div className="scrolling-wrapper">
          <div
            className="scrolling-container"
            style={{ animationDuration: `${Math.max(20, currentMessage.length * 0.25)}s` }}
          >
            <div className="scrolling-message">
              <span>
                <AlertIcon />
                {currentMessage}
              </span>
              <span>
                <AlertIcon />
                {currentMessage}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopBands;
